import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { entityContract } from "@common/contracts/entity.contract";
import type { z } from "zod";
import { Entity } from "@common/types/entity";
import { Workspace } from "@common/types/workspace";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { getAddressQueryKey, queryClient } from "queryClient";
import {
    entitiesAccountsQueryKey,
    updateEntityInQueryData,
    updatePartialEntityInQueryData,
} from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";
import { workspaceQueryKeys } from "./workspace.api";
import { billingQueryKeys } from "@hooks/useBillingStatus";

const entityContractClient = getBackendAPIClient(entityContract);

type UpdateEntityPayload = z.infer<typeof entityContract.updateEntity.body>;

type UpdateEntityAddressPayload = z.infer<
    typeof entityContract.updateEntityAddress.body
>;

type CreateEntityPayload = z.infer<typeof entityContract.createEntity.body>;

async function createEntity(payload: CreateEntityPayload): Promise<Entity> {
    const result = await entityContractClient.createEntity({
        body: payload,
    });
    return result.body;
}

async function updateEntity(
    entityId: number,
    payload: UpdateEntityPayload,
): Promise<Entity> {
    const result = await entityContractClient.updateEntity({
        params: { entityId },
        body: payload,
    });
    return result.body;
}

async function deleteEntity(entityId: number): Promise<void> {
    await entityContractClient.deleteEntity({
        params: { entityId },
    });
}

export async function getAddress(entityId: number) {
    const result = await entityContractClient.getAddress({
        params: { entityId },
    });
    return result.body;
}

export function entityEmailAddressesQueryKey(entityId: number) {
    return ["entity-email-addresses", entityId] as const;
}

export async function fetchEntityEmailAddresses(entityId: number) {
    const result = await entityContractClient.getEmailAddresses({
        params: { entityId },
    });
    return result.body.addresses;
}

export function useEntityEmailAddresses(
    entityId: number | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: entityEmailAddressesQueryKey(entityId ?? 0),
        queryFn: () =>
            entityId == null
                ? Promise.resolve(null)
                : fetchEntityEmailAddresses(entityId),
        enabled: (options?.enabled ?? true) && entityId != null,
    });
}

export function useEntityEmailAddressesForEntities(
    entityIds: number[],
    options?: { enabled?: boolean },
) {
    const isEnabled = options?.enabled ?? true;

    return useQueries({
        queries: entityIds.map((entityId) => ({
            queryKey: entityEmailAddressesQueryKey(entityId),
            queryFn: () => fetchEntityEmailAddresses(entityId),
            enabled: isEnabled,
        })),
    });
}

export function useAddress(
    entityId: number | undefined,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: getAddressQueryKey(entityId ?? 0),
        queryFn: () =>
            entityId == null ? Promise.resolve(null) : getAddress(entityId),
        staleTime: 10 * 60 * 1000, // 10 minutes
        enabled: (options?.enabled ?? true) && entityId != null,
    });
}

async function updateEntityAddress(
    entityId: number,
    address: UpdateEntityAddressPayload["address"],
) {
    const result = await entityContractClient.updateEntityAddress({
        params: { entityId },
        body: { address },
    });
    return result.body;
}

export function useUpdateEntityAddressMutation(entity: Entity) {
    return useMutation({
        mutationFn: (address: UpdateEntityAddressPayload["address"]) =>
            updateEntityAddress(entity.id, address),
        onSuccess: (data) => {
            queryClient.setQueryData(getAddressQueryKey(entity.id), data);
        },
    });
}

type CreateEntityMutationPayload = Omit<CreateEntityPayload, "workspaceId"> &
    Partial<Pick<CreateEntityPayload, "workspaceId">>;

export function useEntityCreationMutation(workspace?: Workspace) {
    return useMutation({
        mutationFn: (data: CreateEntityMutationPayload) => {
            const workspaceId = data.workspaceId ?? workspace?.id;
            if (!workspaceId) {
                throw new Error(
                    "useEntityCreationMutation requires either an explicit workspaceId or a workspace context",
                );
            }

            return createEntity({
                ...data,
                workspaceId,
            });
        },
        onSuccess: (createdEntity, variables) => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            invalidateAccountRelatedQueries({
                entityIds: [createdEntity.id],
                workspaceId: variables.workspaceId ?? workspace?.id,
            });
        },
    });
}

export function useEditEntityMutation(entity: Entity) {
    const { isViewOnly } = useViewOnlyRefusal();

    return useMutation<
        Entity,
        unknown,
        UpdateEntityPayload,
        Entity | undefined
    >({
        mutationFn: (payload: UpdateEntityPayload) =>
            updateEntity(entity.id, payload),
        onMutate: (payload) => {
            // View-only is refused by the API; an optimistic write would only flash.
            if (isViewOnly) {
                return;
            }

            updateEntityInQueryData(queryClient, {
                ...entity,
                ...payload,
            });

            return entity;
        },
        onError: (_1, _2, previousEntity) => {
            if (previousEntity) {
                updateEntityInQueryData(queryClient, previousEntity);
            }
        },
        onSuccess: (updatedEntity) => {
            updateEntityInQueryData(queryClient, updatedEntity);
        },
    });
}

async function updateBookkeepingStartDate(
    entityId: number,
    bookkeepingStartDate: string,
): Promise<Entity> {
    const result = await entityContractClient.updateBookkeepingStartDate({
        params: { entityId },
        body: { bookkeepingStartDate },
    });
    return result.body;
}

export function useUpdateBookkeepingStartDateMutation(entity: Entity) {
    return useMutation({
        mutationFn: (bookkeepingStartDate: string) =>
            updateBookkeepingStartDate(entity.id, bookkeepingStartDate),
        onSuccess: (updatedEntity) => {
            updateEntityInQueryData(queryClient, updatedEntity);
            invalidateAccountRelatedQueries({
                entityIds: [entity.id],
                workspaceId: entity.workspaceId,
            });
        },
    });
}

export function useDeleteEntityMutation(
    entity: Pick<Entity, "id" | "workspaceId">,
) {
    return useMutation({
        mutationFn: async () => await deleteEntity(entity.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
            queryClient.invalidateQueries({
                queryKey: billingQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey: ["billing-organization"],
            });
            invalidateAccountRelatedQueries({
                entityIds: [entity.id],
                workspaceId: entity.workspaceId,
            });
        },
    });
}

async function createLogoUploadUrl(
    entityId: number,
    filename: string,
): Promise<{ url: string; path: string }> {
    const result = await entityContractClient.createLogoUploadUrl({
        params: { entityId },
        body: { name: filename },
    });
    return result.body;
}

async function updateLogo(entityId: number, logoPath: string | null) {
    const result = await entityContractClient.updateLogo({
        params: { entityId },
        body: { logoPath },
    });
    return result.body;
}

export function useCreateLogoUploadUrlMutation(entityId: number) {
    return useMutation({
        mutationFn: (filename: string) =>
            createLogoUploadUrl(entityId, filename),
    });
}

export function useUpdateLogoMutation(entity: Entity) {
    return useMutation({
        mutationFn: (logoPath: string | null) =>
            updateLogo(entity.id, logoPath),
        onSuccess: ({ logoUrl }) => {
            updatePartialEntityInQueryData(queryClient, {
                id: entity.id,
                logo: logoUrl,
            });
        },
    });
}
